import { Controller } from "@hotwired/stimulus"
import { VariousHelpers } from "./concerns/various_helpers"
import { Loggable } from "./concerns/loggable"
import { tns } from 'tiny-slider/src/tiny-slider';

/*
  Controller for tiny-slider

  tiny-slider mutates the DOM, adds wrapper elements, and injects generated
  CSS rules into <head> using the container id. Turbo can cache and restore
  pages while keeping or replacing those nodes, so this controller treats the
  slider instance as disposable: initialize after Stimulus connects, destroy
  before Turbo caches the page, and destroy again when Stimulus disconnects.

  USAGE
    data-controller="slider" (required)
    data-slider-initialized="false" (required) slider hidden by css when false
    data-slider-current-index="0" (required)
    data-slider-loop="false" (optional) defaults to true
    data-slider-config="tiny-slider-options-JSON" (optional) @see https://ganlanyuan.github.io/tiny-slider/
    data-slider-prev-txt=".." (optional)
    data-slider-next-txt=".." (optional)

 */
export default class extends Controller {

  static targets = [ "tlContainer", "container", "prevButton", "nextButton" ]

  /*
    Prepare controller-local state only.

    Stimulus calls initialize before the element is fully in the connected
    lifecycle. tiny-slider reads layout and writes generated CSS, so actual
    slider setup is deferred to connect instead of happening here.
  */
  initialize(){
    VariousHelpers(this);
    Loggable(this, {debug: true});

    this.handleResize = () => this.equalizeHeights();
    this.handleTurboBeforeCache = () => this.destroySlider();
    this.sliderInitializationFrame = null;
  }

  /*
    Attach page-level listeners and schedule the tiny-slider setup.

    The resize listener keeps equal-height cards responsive. turbo:before-cache
    is required because tiny-slider's injected wrappers and <style> rules should
    not be stored in Turbo's snapshot cache.
  */
  connect(){
    window.addEventListener("resize", this.handleResize);
    document.addEventListener("turbo:before-cache", this.handleTurboBeforeCache);
    this.scheduleSliderInitialization();
  }

  /*
    Remove all work owned by this controller.

    Turbo and Stimulus can disconnect controllers without a full page reload.
    Destroying here prevents stale tiny-slider instances, generated CSS, and
    anonymous resize handlers from leaking into the next page state.
  */
  disconnect(){
    this.cancelSliderInitialization();
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener("turbo:before-cache", this.handleTurboBeforeCache);
    this.destroySlider();
  }

  /*
    Build the tiny-slider instance for the current container.

    tiny-slider wraps the container and injects CSS tied to the container id.
    When reconnecting to markup that was already initialized, first unwrap the
    old .tns-outer wrapper to avoid nesting multiple tiny-slider structures.
  */
  initSlider(){
    this.log("initializing slider");

    if (this.containerTarget.children.length === 0) {
      this.log("no slides found")
      return
    }

    this.hideButtonsOnTouch();

    if (this.data.get("initialized") == "true") {
      // avoid nesting on turbo reload
      let oldContainer = $(this.containerTarget).closest(".tns-outer")[0];
      if (oldContainer) {
        this.tlContainerTarget.appendChild(this.containerTarget);
        oldContainer.remove()
      }
    }

    this.slider = tns(this.tinySliderOptions());

    if (!this.slider?.events) {
      this.log("slider failed to initialize")
      return
    }

    this.data.set("initialized", "true"); // con esto, css hace que se torne visible el slider
  }

  /*
    Run tiny-slider setup on the next frame.

    The delay lets Turbo finish swapping/restoring DOM and gives the browser a
    layout-ready element before tiny-slider measures slides and creates its
    generated CSS rules.
  */
  scheduleSliderInitialization(){
    this.cancelSliderInitialization();

    const requestFrame = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));

    this.sliderInitializationFrame = requestFrame(() => {
      this.sliderInitializationFrame = null;

      if (!this.element.isConnected) {
        return;
      }

      this.initSlider();
      this.equalizeHeights();
    });
  }

  /*
    Cancel a scheduled initialization if the element goes away first.

    This prevents a queued requestAnimationFrame callback from initializing a
    slider after Turbo or Stimulus has already removed this controller.
  */
  cancelSliderInitialization(){
    if (this.sliderInitializationFrame === null) {
      return;
    }

    const cancelFrame = window.cancelAnimationFrame || window.clearTimeout;
    cancelFrame(this.sliderInitializationFrame);
    this.sliderInitializationFrame = null;
  }

  /*
    Dispose tiny-slider and reset the visibility flag.

    destroy() removes tiny-slider's generated <style> tag and restores the
    original container markup. Resetting initialized hides the slider until the
    next clean initialization finishes.
  */
  destroySlider(){
    this.cancelSliderInitialization();

    if (this.slider?.destroy) {
      this.slider.destroy();
    }

    this.slider = null;
    this.data.set("initialized", "false");
  }

  /*
    Make visible cards the same height.

    Slider cards can mix titles, metadata, and optional covers. Reset first so
    the measurement reflects natural content height, then apply the largest
    height to every .flexContainer in this slider.
  */
  equalizeHeights(){
    this.log("equalizing heights");
    let maxHeight = 0;

    // reset heights
    this.containerTarget.querySelectorAll(".card").forEach(card => {
      let c = card.querySelector(".flexContainer")
      c.style.height = null;
    })

    // find max height
    this.containerTarget.querySelectorAll(".card").forEach(card => {
      let c = card.querySelector(".flexContainer")
      if (c.offsetHeight > maxHeight) {
        maxHeight = c.offsetHeight;
      }
    })

    // set heights to max height
    this.containerTarget.querySelectorAll(".card").forEach(card => {
      let c = card.querySelector(".flexContainer")
      c.style.height = maxHeight + "px";
    })
  }

  /*
    Compose tiny-slider options from defaults and optional data config.

    The defaults reflect this app's Bootstrap breakpoints and card layout.
    Per-slider data config can override top-level options and individual
    responsive breakpoint settings without duplicating the whole object.
  */
  tinySliderOptions(){
    let config = {
      container: this.containerTarget,

      gutter: 0,
      autoHeight: true, // necesario si ningun post tiene cover

      freezable: false, // necesario para que funcione si cambian el tamaño de la pantalla y se freezaria
      slideBy: 1,
      startIndex: this.data.get("currentIndex"),

      preventScrollOnTouch: "auto",
      swipeAngle: 75,

      mouseDrag: true,
      arrowKeys: false,
      nav: false,
      loop: this.loopConfig(),

      controls: this.controlsConfig(),
      prevButton: this.prevButtonConfig(),
      nextButton: this.nextButtonConfig(),

      responsive: {
        // bootstrap breakpoint: xs: 0 , sm: 576, md: 768, lg: 992, xl: 1200
        0: {
          items: 2,
          edgePadding: 25,
        },
        768: {
          items: 3,
          edgePadding: 30,
        },
        992: {
          items: 3,
          edgePadding: 50,
        },
        1200: {
          items: 4,
          edgePadding: 50,
        },
        1400: {
          items: 4,
          edgePadding: 50,
        }
      }
    }

    // merge userConfig into defaultConfig
    let userConfig = this.data.get("config");
    if (userConfig) {
      userConfig = JSON.parse(userConfig);
      for (let attrname in userConfig) {
        if (attrname === "responsive") {
          for (let breakpoint in userConfig[attrname]) {
            for (let subAttrname in userConfig[attrname][breakpoint]) {
              config[attrname][breakpoint][subAttrname] = userConfig[attrname][breakpoint][subAttrname];
            }
          }
        } else {
          config[attrname] = userConfig[attrname];
        }
      }
    }

    return config;
  }

  /*
    Hide external prev/next controls on touch devices.

    Swipe is the primary mobile interaction, and the side controls consume
    horizontal space. The target container is widened so touch layouts can use
    the full viewport.
  */
  hideButtonsOnTouch(){
    if (this.isTouchEnabled()){
      if (this.hasPrevButtonTarget){
        this.prevButtonTarget.style.display = "none";
      }
      if (this.hasPrevButtonTarget){
        this.nextButtonTarget.style.display = "none";
      }
      if (this.hasTlContainerTarget){
        this.tlContainerTarget.style.maxWidth = "100%";
      }
    }
  }

  /*
    Enable tiny-slider controls only when both buttons exist and touch controls
    are not preferred.
  */
  controlsConfig(){
    return this.hasPrevButtonTarget && this.hasNextButtonTarget && !this.isTouchEnabled();
  }

  /*
    Return the previous button element for tiny-slider or false to let
    tiny-slider run without a previous control.
  */
  prevButtonConfig(){
    if (this.hasPrevButtonTarget && !this.isTouchEnabled()) {
      return this.prevButtonTarget;
    } else {
      return false;
    }
  }

  /*
    Return the next button element for tiny-slider or false to let tiny-slider
    run without a next control.
  */
  nextButtonConfig(){
    if (this.hasNextButtonTarget && !this.isTouchEnabled()) {
      return this.nextButtonTarget;
    } else {
      return false;
    }
  }

  /*
    Decide whether the slider should loop.

    Sliders loop by default unless data-slider-loop explicitly opts out.
  */
  loopConfig(){
    if(this.data.get("loop")){
      return this.toBool(this.data.get("loop"))
    } else  {
      return true
    }
  }

  /*
    Normalize HTML data values into booleans.

    Data attributes arrive as strings, while tests or programmatic callers may
    pass actual booleans.
  */
  toBool(val){
    return ["true",true,"1","yes"].includes(val)
  }

}
